import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Alert, Tab, Tabs, Modal } from 'react-bootstrap';
import { ChevronDown, ChevronRight, Star, StarFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

const ReviewSummaryPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [rawSubmissions, setRawSubmissions] = useState([]);
  const [gradingCriteria, setGradingCriteria] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [modalComment, setModalComment] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('');
  const [expandedSubmissions, setExpandedSubmissions] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Process submissions when gradingCriteria is available
    if (gradingCriteria.length > 0 && rawSubmissions.length > 0) {
      const processedSubmissions = processSubmissions(rawSubmissions);
      setSubmissions(processedSubmissions);
    }
  }, [gradingCriteria, rawSubmissions]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch grading criteria
      await fetchGradingCriteria();
      
      // Fetch submissions with reviews
      await fetchSubmissionsWithReviews();
      
      // Fetch tracks
      await fetchTracks();
      
    } catch (err) {
      console.error('Error fetching review summary data:', err);
      setError('Failed to load grading results data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGradingCriteria = async () => {
    try {
      const response = await fetch(`${config.api.url}admin/challenge-config?format=json`);
      if (response.ok) {
        const data = await response.json();
        if (data.gradingCriteria && data.gradingCriteria.length > 0) {
          setGradingCriteria(data.gradingCriteria);
        } else {
          // Default criteria if none configured
          setGradingCriteria([
            { name: 'Technical Implementation', weight: 40 },
            { name: 'Innovation & Creativity', weight: 30 },
            { name: 'Documentation & Presentation', weight: 20 },
            { name: 'Practical Impact', weight: 10 }
          ]);
        }
      }
    } catch (err) {
      console.error('Error fetching grading criteria:', err);
      // Use default criteria
      setGradingCriteria([
        { name: 'Technical Implementation', weight: 40 },
        { name: 'Innovation & Creativity', weight: 30 },
        { name: 'Documentation & Presentation', weight: 20 },
        { name: 'Practical Impact', weight: 10 }
      ]);
    }
  };

  const fetchSubmissionsWithReviews = async () => {
    try {
      const response = await fetch(`${config.api.url}admin/review-summary?format=json`);
      if (response.ok) {
        const data = await response.json();
        setRawSubmissions(data.submissions || []);
      } else {
        throw new Error(`HTTP ${response.status}: Failed to fetch submissions`);
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
    }
  };

  const fetchTracks = async () => {
    try {
      const response = await fetch(`${config.api.url}admin/tracks?format=json`);
      if (response.ok) {
        const data = await response.json();
        const trackList = data.tracks || ['Track 1', 'Track 2', 'Track 3'];
        setTracks(trackList);
        if (trackList.length > 0 && !activeTab) {
          setActiveTab(trackList[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching tracks:', err);
      const defaultTracks = ['Track 1', 'Track 2', 'Track 3'];
      setTracks(defaultTracks);
      if (!activeTab) {
        setActiveTab(defaultTracks[0]);
      }
    }
  };

  const processSubmissions = (rawSubmissions) => {
    return rawSubmissions.map(submission => {
      const processedSubmission = {
        ...submission,
        criteriaAverages: {},
        overallScore: 0,
        reviewCount: submission.reviews ? submission.reviews.length : 0,
        pendingReviews: submission.reviews ? submission.reviews.filter((r)=>!r.criteriaScores || r.isDraft).length : 0,
      };

      console.log(submission.reviews);
      console.log(gradingCriteria);
      if (submission.reviews && submission.reviews.length > 0) {
        // Calculate average for each criteria
        gradingCriteria.forEach((criteria, index) => {
          const scores = submission.reviews
            .map(review => review.criteriaScores && review.criteriaScores[index])
            .filter(score => score !== undefined && score !== null);
          console.log("scores", scores);
          
          if (scores.length > 0) {
            processedSubmission.criteriaAverages[index] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
          } else {
            processedSubmission.criteriaAverages[index] = 0;
          }
        });

        // Calculate weighted overall score using CURRENT criteria weights
        let totalWeightedScore = 0;
        let totalWeight = 0;
        
        gradingCriteria.forEach((criteria, index) => {
          if (processedSubmission.criteriaAverages[index] > 0) {
            totalWeightedScore += processedSubmission.criteriaAverages[index] * criteria.weight;
          }
          totalWeight += criteria.weight;
        });

        // Use live calculation instead of stored overallScore
        processedSubmission.overallScore = totalWeight > .99 ? totalWeightedScore / totalWeight : 0;
      }

      return processedSubmission;
    });
  };

  const toggleSubmissionExpansion = (submissionId) => {
    const newExpanded = new Set(expandedSubmissions);
    if (newExpanded.has(submissionId)) {
      newExpanded.delete(submissionId);
    } else {
      newExpanded.add(submissionId);
    }
    setExpandedSubmissions(newExpanded);
  };

  const showComment = (comment, title) => {
    setModalComment(comment || 'No comment provided');
    setModalTitle(title);
    setShowCommentModal(true);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedSubmissions = (submissions) => {
    if (!sortConfig.key) return submissions;

    return [...submissions].sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'title') {
        aValue = a.title || '';
        bValue = b.title || '';
      } else if (sortConfig.key === 'authorName') {
        aValue = a.authorName || '';
        bValue = b.authorName || '';
      } else if (sortConfig.key === 'overallScore') {
        aValue = a.overallScore || 0;
        bValue = b.overallScore || 0;
      } else if (sortConfig.key === 'reviewCount') {
        aValue = a.reviewCount || 0;
        bValue = b.reviewCount || 0;
      } else if (sortConfig.key.startsWith('criteria-')) {
        const criteriaIndex = parseInt(sortConfig.key.split('-')[1]);
        aValue = a.criteriaAverages?.[criteriaIndex] || 0;
        bValue = b.criteriaAverages?.[criteriaIndex] || 0;
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  };

  const renderStarRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarFill key={i} className="text-warning" size={14} />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} className="text-warning" size={14} />);
      } else {
        stars.push(<Star key={i} className="text-muted" size={14} />);
      }
    }

    return (
      <span className="d-flex align-items-center">
        {stars}
        <small className="ms-1 text-muted">({rating.toFixed(1)})</small>
      </span>
    );
  };

  const getSubmissionsByTrack = (track) => {
    const trackSubmissions = submissions.filter(submission => submission.track === track);
    return getSortedSubmissions(trackSubmissions);
  };

  const renderSubmissionRow = (submission, index) => {
    const isExpanded = expandedSubmissions.has(submission.id);
    const hasReviews = submission.reviews && submission.reviews.length > 0;
    const hasPendingReviews = submission.pendingReviews > 0;

    return (
      <React.Fragment key={submission.id}>
        <tr 
          className="cursor-pointer"
          onClick={() => toggleSubmissionExpansion(submission.id)}
          style={{ cursor: 'pointer' }}
        >
          <td>
            <div className="d-flex align-items-center">
              <span className="me-2">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <div>
                <div className="fw-bold">{submission.title}</div>
                <small className="text-muted">{submission.authorName}</small>
              </div>
            </div>
          </td>
          
          {/* Criteria columns */}
          {gradingCriteria.map((criteria, criteriaIndex) => (
            <td key={criteriaIndex} className="text-center">
              {submission.criteriaAverages[criteriaIndex] !== undefined && submission.criteriaAverages[criteriaIndex] !== null ? (
                submission.criteriaAverages[criteriaIndex].toFixed(1)
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          ))}
          
          {/* Overall score */}
          <td className="text-center">
            {submission.overallScore && submission.overallScore > 0 ? (
              <div>
                <strong className="text-primary">{submission.overallScore.toFixed(2)}</strong>
              </div>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
          
          {/* Review status */}
          <td className="text-center">
            <div>
              {(submission.reviewCount > submission.pendingReviews) && 
                <Badge bg="success" className="me-1">{submission.reviewCount-submission.pendingReviews}</Badge>
              }
              {submission.pendingReviews > 0 && (
                <Badge bg="warning">{submission.pendingReviews} pending</Badge>
              )}
            </div>
          </td>
        </tr>
        
        {/* Expanded review details */}
        {isExpanded && (
          <>
            {/* Show message if no reviews assigned */}
            {!hasReviews && !hasPendingReviews && (
              <tr style={{background:'#eee'}}>
                <td colSpan={gradingCriteria.length + 3} className="text-center py-3">
                  <em className="text-muted">No reviews assigned</em>
                </td>
              </tr>
            )}
            
            {/* reviews */}
            {hasReviews && submission.reviews.map((review, reviewIndex) => {
              var reviewOverall = null;
              if (review.criteriaScores && !review.isDraft) {
                reviewOverall = gradingCriteria.reduce((total, criteria, index) => {
                  return total + (review.criteriaScores[index] * criteria.weight);
                }, 0) / gradingCriteria.reduce((total, criteria) => total + criteria.weight, 0);
              }
              return (
                <tr key={reviewIndex} style={{background:'#eee'}}>
                  <td>
                    <strong>{(review.reviewerInfo && review.reviewerInfo['email']) || '[assigned]'}</strong>
                  </td>
                  {gradingCriteria.map((criteria, criteriaIndex) => (
                    <td key={criteriaIndex} className="text-center">
                      {review.criteriaScores[criteriaIndex] ? (
                        <div 
                          className="d-flex justify-content-center cursor-pointer" 
                          onClick={() => showComment(
                            review.criteriaComments?.[criteriaIndex], 
                            `${criteria.name} - ${review.reviewerInfo['full_name']}`
                          )}
                          title="Click to view comment"
                        >
                          {renderStarRating(review.criteriaScores[criteriaIndex])}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  ))}
                  <td className="text-center">
                    <div
                      className="cursor-pointer" 
                      onClick={() => showComment(
                        review.generalComments, 
                        `General Comments - ${review.reviewerName || 'Anonymous'}`
                      )}
                      title="Click to view general comments"
                    >
                      {reviewOverall ? reviewOverall.toFixed(2) : '-'}
                    </div>
                  </td>
                  <td className="text-center">
                    { (review.criteriaScores && !review.isDraft) ? 
                      <Badge bg="success">completed</Badge> :
                      <Badge bg="warning">pending</Badge>
                    }
                  </td>
                </tr>
              );
            })}
          </>
        )}
      </React.Fragment>
    );
  };

  const renderTrackTable = (track) => {
    const trackSubmissions = getSubmissionsByTrack(track);

    if (trackSubmissions.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No submissions found for {track}</p>
        </div>
      );
    }

    return (
      <Table responsive hover className="mb-0">
        <thead className="table-dark">
          <tr>
            <th 
              className="cursor-pointer" 
              onClick={() => handleSort('title')}
              title="Click to sort by submission title"
            >
              Submission
              {sortConfig.key === 'title' && (
                <span className="ms-1">
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            {gradingCriteria.map((criteria, index) => (
              <th 
                key={index} 
                className="text-center cursor-pointer"
                onClick={() => handleSort(`criteria-${index}`)}
                title={`Click to sort by ${criteria.name}`}
              >
                {criteria.name}
                {sortConfig.key === `criteria-${index}` && (
                  <span className="ms-1">
                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </span>
                )}
                {false && <small className="text-muted">({criteria.weight}%)</small>}
              </th>
            ))}
            <th 
              className="text-center cursor-pointer"
              onClick={() => handleSort('overallScore')}
              title="Click to sort by overall score"
            >
              Overall Score
              {sortConfig.key === 'overallScore' && (
                <span className="ms-1">
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th 
              className="text-center cursor-pointer"
              onClick={() => handleSort('reviewCount')}
              title="Click to sort by review count"
            >
              Reviews
              {sortConfig.key === 'reviewCount' && (
                <span className="ms-1">
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {trackSubmissions.map((submission, index) => renderSubmissionRow(submission, index))}
        </tbody>
      </Table>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading grading results..." />;
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Grading Results</h2>
              <p className="text-muted">
                Overview of all submissions with calculated scores based on grading criteria
              </p>
            </div>
            <div>
              <Button 
                variant="outline-primary" 
                onClick={() => navigate('/admin/review')}
              >
                Back to Reviews
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* Summary Statistics */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-primary">{submissions.length}</h4>
                  <small className="text-muted">Submissions</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-success">
                    {submissions.reduce((sum,s) => sum + s.reviews.filter((r)=>r.isDraft===false).length,0)}
                  </h4>
                  <small className="text-muted">Reviews Completed</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-warning">
                    {submissions.reduce((sum, s) => sum + s.reviews.filter((r)=>!r.criteriaScores||r.isDraft).length,0)}
                  </h4>
                  <small className="text-muted">Reviews Pending</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-info">
                    {submissions.filter(s => s.overallScore > 0).length > 0 ? 
                      (submissions
                        .filter(s => s.overallScore > 0)
                        .reduce((sum, s) => sum + s.overallScore, 0) / 
                       submissions.filter(s => s.overallScore > 0).length
                      ).toFixed(2) : '0.00'
                    }
                  </h4>
                  <small className="text-muted">Average Score</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabbed Tables by Track */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Submissions by Track</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Tabs 
                activeKey={activeTab} 
                onSelect={setActiveTab}
                className="px-3 pt-3"
              >
                {tracks.map(track => (
                  <Tab 
                    key={track} 
                    eventKey={track} 
                    title={
                      <span>
                        {track}
                        <Badge bg="secondary" className="ms-2">
                          {getSubmissionsByTrack(track).length}
                        </Badge>
                      </span>
                    }
                  >
                    <div className="p-3">
                      {renderTrackTable(track)}
                    </div>
                  </Tab>
                ))}
              </Tabs>
            </Card.Body>
          </Card>

          {/* Instructions */}
          <Card className="mt-4">
            <Card.Body>
              <h6>How Scores Are Calculated</h6>
              <ul className="mb-0">
                <li>Each criteria is averaged across all reviews for that submission</li>
                <li>Overall score is calculated using the weighted average based on criteria weights</li>
                <li>You can sort the submissions by clicking one of the column headers.</li>
                <li>Click on submissions with reviews to see detailed reviewer feedback</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Comment Modal */}
      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{__html:modalComment}} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReviewSummaryPage;
